import { DownloadOutlined } from "@ant-design/icons";
import { message, Table } from "antd";
import axios from "axios";
import ExcelJS from "exceljs";
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import * as util from "util";
import logo from "../../assets/legalpanel-dark.png";
import useFormatter from "../../hooks/useFormatter";
import innerText from "../../util/innerText";
import ActionButton from "../action-button";
import styles from "./_styles.less";

function MiniTable(
  { columns, dataSource, expandable, loading, rowSelection, scroll, style, onRow, ...props },
  ref
) {
  const { t } = useTranslation();
  const divRef = useRef(null);
  const history = useHistory();
  const formatter = useFormatter();
  const [pageSize, setPageSize] = useState(50);
  const [scrollY, setScrollY] = useState(scroll?.y);

  useImperativeHandle(
    ref,
    () => ({
      download: async (customName) => {
        try {
          const logoBuffer = (
            await axios.get(logo, { baseURL: null, responseType: "arraybuffer" })
          ).data;

          let wbNome = [document.title];
          let wsNome;
          const pathname = history.location.pathname;
          const index = pathname.lastIndexOf("/");
          if (index >= 0) {
            wsNome = pathname.substring(index + 1);
            wbNome.push(wsNome);
          }
          wbNome.push(new Date().toLocaleString());
          wbNome = wbNome.join(" - ");

          const wb = new ExcelJS.Workbook();
          const logoImageId = wb.addImage({
            buffer: logoBuffer,
            extension: "png",
          });

          const ws = wb.addWorksheet(wsNome);
          ws.addImage(logoImageId, {
            tl: { col: 0.25, row: 0.5 },
            editAs: "absolute",
            ext: { width: 120, height: 24 },
            hyperlinks: {
              hyperlink: location.origin,
              tooltip: location.origin,
            },
          });
          const headerRow = ws.getRow(1);
          headerRow.height = 28;
          headerRow.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF262626" },
          };

          const cols = columns.map((column) => ({
            ...column,
            width: column.title?.length ?? 1,
            getCellValue(row) {
              let width = 8;
              let text;
              if (column.dataIndex instanceof Array) text = column.dataIndex.reduce((pv, cv) => pv && pv[cv], row);
              else text = row[column.dataIndex];
              if (typeof column.render === "function") text = column.render(text, row);
              text = innerText(text);
              if (column.width) width += Math.ceil(column.width / 15);
              else if (text?.length) width += text.length;
              this.width = Math.max(this.width, width);
              return text;
            },
          }));

          const getCols = (row, depth) =>
            cols.map((column, i) => {
              let value = column.getCellValue(row);
              try {
                if (/^-?[\d.,]+$/.test(value)) {
                  const number = formatter.parseNumber(value);
                  if (isFinite(number)) return number;
                }
              } catch (e) {}
              if (depth > 0 && i === 0) value = " ".repeat(4 * depth) + value;
              return value;
            });

          const getRows = (rows, depth = 0) => {
            const result = [];
            for (const row of rows) {
              result.push(getCols(row, depth));
              const children = row[expandable?.childrenColumnName];
              if (children?.length > 0) result.push(...getRows(children, depth + 1));
            }
            return result;
          };

          ws.addTable({
            name: wsNome,
            ref: "A2",
            headerRow: true,
            style: {
              theme: "TableStyleLight1",
              showRowStripes: true,
            },
            columns: cols.map((column) => ({
              name: column.title,
              filterButton: true,
            })),
            rows: getRows(dataSource),
          });

          for (let i = 0; i < ws.columns.length; i++) {
            ws.columns[i].width = cols[i].width + 8;
          }

          const buffer = await wb.xlsx.writeBuffer();
          const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
          const a = document.createElement("a");
          a.style.display = "none";
          a.setAttribute("href", window.URL.createObjectURL(blob));
          const finalName = customName ? `${customName}.xlsx` : `${wbNome}.xlsx`;
          a.setAttribute("download", finalName);
          a.click();

          await new Promise((resolve) => setTimeout(resolve, 5000));
        } catch (e) {
          console.error(e);
        }
      },
    }),
    [columns, dataSource, expandable, loading]
  );

  useLayoutEffect(() => {
    function resize() {
      if (scroll?.y === "auto") setScrollY(0);
    }

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [divRef.current, dataSource?.length, scroll]);
  //
  useEffect(() => {
    if (scroll?.y === "auto" && scrollY === 0) {
      let offsetY = -1;
      const title = divRef.current.querySelector(".ant-table-title");
      const thead = divRef.current.querySelector(".ant-table-thead");
      const pagination = divRef.current.querySelector(".ant-pagination");
      if (title) offsetY -= title.clientHeight + 8;
      if (thead) offsetY -= thead.clientHeight;
      if (pagination) offsetY -= pagination.clientHeight + 16;
      setScrollY(divRef.current.clientHeight + offsetY);
    }
  }, [scrollY]);

  const handleRow = (record, rowIndex) => {
    let fn;
    if (typeof onRow === "function") fn = onRow(record, rowIndex);
    return {
      onClick: (event) => {
        if (typeof fn?.onClick === "function") fn?.onClick(event);
        else event.currentTarget.classList.toggle("focused");
      },
      onDoubleClick: (event) => {
        if (typeof fn?.onDoubleClick === "function") fn?.onDoubleClick(event);
      },
      onContextMenu: (event) => {
        if (typeof fn?.onContextMenu === "function") fn?.onContextMenu(event);
      },
      onMouseEnter: (event) => {
        if (typeof fn?.onMouseEnter === "function") fn?.onMouseEnter(event);
      },
      onMouseLeave: (event) => {
        if (typeof fn?.onMouseLeave === "function") fn?.onMouseLeave(event);
      },
    };
  };

  const showTotal = (total, range) => {
    let messageText =
      total === 1
        ? util.format(t("exibindo %d item"), total)
        : util.format(t("exibindo %d-%d de %d itens"), range[0], range[1], total);

    const selecionados = rowSelection?.selectedRowKeys?.length ?? 0;
    if (selecionados === 1)
      messageText = util.format(t("%d selecionado, %s"), selecionados, messageText);
    else if (selecionados > 1)
      messageText = util.format(t("%d selecionados, %s"), selecionados, messageText);

    return messageText;
  };

  return (
    <div ref={divRef} className={styles["mini-table"]} style={style}>
      <Table
        columns={columns.filter((col) => !col.hidden)}
        dataSource={dataSource}
        expandable={expandable}
        loading={loading}
        pagination={{
          pageSize,
          showTotal,
          showSizeChanger: false,
        }}
        rowKey="id"
        rowSelection={rowSelection}
        scroll={{ x: scroll?.x ?? true, y: scrollY }}
        onRow={handleRow}
        {...props}
      />
    </div>
  );
}

function DownloadButton({ miniTableRef, disabled, customFileName, ...props }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const handleClick = () => {
    setLoading(true);
    miniTableRef.current
      .download(customFileName)
      .catch((err) => message.error(err))
      .finally(() => setLoading(false));
  };

  return (
    <ActionButton
      disabled={disabled}
      noTitle
      title={t("Baixar Dados")}
      icon={<DownloadOutlined />}
      loading={loading}
      onClick={handleClick}
      {...props}
    />
  );
}

MiniTable = forwardRef(MiniTable);
MiniTable.DownloadButton = DownloadButton;

export default MiniTable;
